import { useEffect, useState } from "react";
import { useScrollEnd } from "@/hooks/useScrollEnd";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/ui/password-input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { TermsAndConditions } from "@/components/TermsAndConditions";
import { lovable } from "@/integrations/lovable";
import { reportClientError } from "@/lib/error-reporter";
import { syncViewModeForAuthReturn } from "@/hooks/useViewMode";
import { toast } from "sonner";
import { Eye, Loader2, MailCheck, PartyPopper } from "lucide-react";

export const SIGN_IN_DIALOG_OPEN_EVENT = "openSignInDialog";
export const SIGN_IN_DIALOG_SUCCESS_EVENT = "signInDialogSuccess";

export type SignInDialogPayload = {
  /** Optional message shown above the form to give context (e.g. "Sign in to report this event") */
  message?: string;
  /** Which tab to open on (defaults to signin) */
  initialTab?: "signin" | "signup";
  /** Which account type is preselected on the signup tab (defaults to attendee) */
  defaultAccountType?: "attendee" | "organizer";
  /** Custom callback event name fired on success (in addition to SIGN_IN_DIALOG_SUCCESS_EVENT) */
  successEvent?: string;
  /** Where Google OAuth should return to (defaults to current location) */
  oauthReturnTo?: string;
  /** Event modal to reopen after an OAuth redirect */
  restoreEventModal?: {
    id: string;
    slug?: string | null;
    title: string;
    canManage?: boolean;
    defaultView?: "preview" | "edit";
  };
};

export const openSignInDialog = (payload?: SignInDialogPayload) => {
  window.dispatchEvent(new CustomEvent(SIGN_IN_DIALOG_OPEN_EVENT, { detail: payload || {} }));
};

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23 7.7 23 3.99 20.53 2.18 18.07z"/>
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

const AppleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

export const SignInDialogMount = () => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<SignInDialogPayload>({});
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  // When the user backs out of the Apple/Google sign-in page, the browser
  // restores this page from its back-forward cache with the spinner still on.
  // Reset the loading states whenever the page is restored that way.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsGoogleLoading(false);
        setIsAppleLoading(false);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  // Sign-up state
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSignUpLoading, setIsSignUpLoading] = useState(false);
  const [signUpEmailSent, setSignUpEmailSent] = useState(false);
  const [accountType, setAccountType] = useState<"attendee" | "organizer">("attendee");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SignInDialogPayload>).detail || {};
      setPayload(detail);
      setTab(detail.initialTab === "signup" ? "signup" : "signin");
      setEmail("");
      setPassword("");
      setSignUpEmail("");
      setSignUpPassword("");
      setTermsAccepted(false);
      setSignUpEmailSent(false);
      setAccountType(detail.defaultAccountType === "organizer" ? "organizer" : "attendee");
      setFirstName("");
      setLastName("");
      setBusinessName("");
      setBusinessDescription("");
      setOpen(true);
    };
    window.addEventListener(SIGN_IN_DIALOG_OPEN_EVENT, handler);
    return () => window.removeEventListener(SIGN_IN_DIALOG_OPEN_EVENT, handler);
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password");
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const returnTo = payload.oauthReturnTo || window.location.pathname + window.location.search;
      syncViewModeForAuthReturn(returnTo);
      toast.success("Welcome back!", { duration: 2000 });
      // Notify listeners so they can resume the action that needed auth
      window.dispatchEvent(new CustomEvent(SIGN_IN_DIALOG_SUCCESS_EVENT));
      if (payload.successEvent) {
        const successEvent = payload.successEvent;
        // Small delay to allow auth state to propagate
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent(successEvent));
        }, 200);
      }
      setOpen(false);
    } catch (err: any) {
      reportClientError({ message: err?.message || "Sign in failed", stack: err?.stack, component: "Auth.SignInDialog", severity: "warning", metadata: { email } });
      toast.error(err?.message || "Sign in failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpEmail || !signUpPassword) {
      toast.error("Enter your email and password");
      return;
    }
    if (signUpPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Enter your first and last name");
      return;
    }
    if (accountType === "organizer" && (!businessName.trim() || !businessDescription.trim())) {
      toast.error("Enter your organizer name and description");
      return;
    }
    setIsSignUpLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: signUpEmail,
        password: signUpPassword,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            account_type: accountType === "organizer" ? "professional" : "attendee",
            professional_type: accountType === "organizer" ? "organizer" : null,
            business_name: accountType === "organizer" ? businessName.trim() : null,
            business_description: accountType === "organizer" ? businessDescription.trim() : null,
          },
        },
      });
      if (error) throw error;
      // Email confirmation is enabled - no session yet
      setSignUpEmailSent(true);
    } catch (err: any) {
      reportClientError({ message: err?.message || "Sign up failed", stack: err?.stack, component: "Auth.SignInDialog", severity: "warning", metadata: { email: signUpEmail } });
      toast.error(err?.message || "Sign up failed");
    } finally {
      setIsSignUpLoading(false);
    }
  };

  const handleOAuth = async (provider: "google" | "apple") => {
    const setLoading = provider === "google" ? setIsGoogleLoading : setIsAppleLoading;
    setLoading(true);
    try {
      if (tab === "signup") {
        localStorage.setItem("signup_intent", JSON.stringify({
          accountType: accountType === "organizer" ? "professional" : "attendee",
          professionalType: accountType === "organizer" ? "organizer" : null,
        }));
      }
      // Persist intended return path so AuthCallback can restore it
      const returnTo = payload.oauthReturnTo || window.location.pathname + window.location.search;
      try {
        sessionStorage.setItem("oauth_return_to", returnTo);
        if (payload.restoreEventModal?.id) {
          sessionStorage.setItem(
            "pendingEventPurchaseModal",
            JSON.stringify({ event: payload.restoreEventModal, returnTo })
          );
        }
      } catch {
        // Continue without persisted return state if storage is unavailable.
      }
      const callbackUrl = new URL(`${window.location.origin}/auth/callback`);
      callbackUrl.searchParams.set("intent", tab === "signup" ? "signup" : "signin");
      callbackUrl.searchParams.set("returnTo", returnTo);
      if (payload.restoreEventModal?.id) {
        callbackUrl.searchParams.set("modalEventId", payload.restoreEventModal.id);
        callbackUrl.searchParams.set("modalEventTitle", payload.restoreEventModal.title);
        if (payload.restoreEventModal.slug) callbackUrl.searchParams.set("modalEventSlug", payload.restoreEventModal.slug);
        if (payload.restoreEventModal.canManage) callbackUrl.searchParams.set("modalEventCanManage", "true");
        if (payload.restoreEventModal.defaultView) callbackUrl.searchParams.set("modalEventDefaultView", payload.restoreEventModal.defaultView);
      }
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: callbackUrl.toString(),
      });
      if (result.error) throw result.error;
      if (!result.redirected) {
        window.location.href = `${callbackUrl.pathname}${callbackUrl.search}`;
      }
    } catch (err: any) {
      toast.error(err?.message || `${provider === "google" ? "Google" : "Apple"} sign in failed`);
      setLoading(false);
    }
  };

  // The form panel hides its scroll bar and instead darkens at its bottom edge
  // until you reach the end, so the hint matches the create-event window.
  const { atEnd: atPanelEnd, attach: attachPanel } = useScrollEnd({ observeCharacterData: true });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[min(24rem,calc(100%-2.5rem))] sm:max-w-lg max-h-[90dvh] !flex flex-col overflow-hidden rounded-3xl pt-8 bg-card/90 backdrop-blur-2xl shadow-2xl shadow-black/50 border-foreground/10">
        <DialogHeader className="text-center sm:text-center shrink-0">
          <DialogTitle className="text-2xl">Welcome to Nightspy</DialogTitle>
          <DialogDescription>Know what's happening in your city</DialogDescription>
          {payload.message && <p className="pt-1 text-sm text-muted-foreground">{payload.message}</p>}
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "signin" | "signup")} className="flex flex-col min-h-0 flex-1">
          <TabsList className="grid w-full grid-cols-2 shrink-0">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          <div ref={attachPanel} className="no-scrollbar mt-4 max-h-[calc(90dvh-10rem)] min-h-0 overflow-y-auto overscroll-contain">
            <div className="px-1 py-0.5">
            <TabsContent value="signin" className="mt-0">
            <form onSubmit={handleSignIn} className="space-y-4">
              <OAuthButtons mode="signin" onOAuth={handleOAuth} googleLoading={isGoogleLoading} appleLoading={isAppleLoading} />
              <OrDivider />
              <div className="space-y-2">
                <Label htmlFor="signin-modal-email">Email</Label>
                <Input
                  id="signin-modal-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0"
                />
              </div>
              <div className="space-y-2 pb-[0.42rem]">
                <Label htmlFor="signin-modal-password">Password</Label>
                <PasswordInput
                  id="signin-modal-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="signup" className="mt-0">
            {signUpEmailSent ? (
              <div className="flex flex-col items-center text-center gap-3 py-4">
                <MailCheck className="h-10 w-10 text-primary" />
                <p className="font-medium">Check your email</p>
                <p className="text-sm text-muted-foreground">
                  We sent a confirmation link to <span className="font-medium text-foreground">{signUpEmail}</span>. Click it to activate your account, then sign in.
                </p>
                <Button variant="outline" className="mt-2" onClick={() => setTab("signin")}>
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="mx-auto grid w-full max-w-xs grid-cols-2 gap-1.5 rounded-2xl bg-muted p-1.5">
                  <Button type="button" variant="ghost" aria-pressed={accountType === "attendee"} onClick={() => setAccountType("attendee")} className={cn("h-auto flex-col p-4", accountType === "attendee" ? "rounded-xl bg-background text-foreground shadow-sm hover:bg-background" : "rounded-xl text-muted-foreground hover:text-foreground")}>
                    <Eye className={`mb-2 h-6 w-6 ${accountType === "attendee" ? "text-foreground" : "text-muted-foreground"}`} />
                    <span>Attendee</span><span className="text-xs font-normal text-muted-foreground">Discover events</span>
                  </Button>
                  <Button type="button" variant="ghost" aria-pressed={accountType === "organizer"} onClick={() => setAccountType("organizer")} className={cn("h-auto flex-col p-4", accountType === "organizer" ? "rounded-xl bg-background text-foreground shadow-sm hover:bg-background" : "rounded-xl text-muted-foreground hover:text-foreground")}>
                    <PartyPopper className={`mb-2 h-6 w-6 ${accountType === "organizer" ? "text-foreground" : "text-muted-foreground"}`} />
                    <span>Organizer</span><span className="text-xs font-normal text-muted-foreground">Create and host</span>
                  </Button>
                </div>
                {accountType === "organizer" && <p className="pb-3 text-center text-xs font-medium text-warning">Organizer accounts require approval</p>}
                <OAuthButtons mode="signup" onOAuth={handleOAuth} googleLoading={isGoogleLoading} appleLoading={isAppleLoading} />
                <OrDivider />
                {accountType === "organizer" && (
                  <>
                    <div className="space-y-2"><Label htmlFor="signup-modal-business">Organizer Name</Label><Input id="signup-modal-business" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0" /></div>
                    <div className="space-y-2"><Label htmlFor="signup-modal-description">Description</Label><Textarea id="signup-modal-description" value={businessDescription} onChange={(e) => setBusinessDescription(e.target.value)} rows={2} required className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0" /></div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label htmlFor="signup-modal-first-name">First Name</Label><Input id="signup-modal-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0" /></div>
                  <div className="space-y-2"><Label htmlFor="signup-modal-last-name">Last Name</Label><Input id="signup-modal-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0" /></div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-modal-email">Email</Label>
                  <Input
                    id="signup-modal-email"
                    type="email"
                    autoComplete="email"
                    value={signUpEmail}
                    onChange={(e) => setSignUpEmail(e.target.value)}
                    required
                    className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-modal-password">Password</Label>
                  <PasswordInput
                    id="signup-modal-password"
                    autoComplete="new-password"
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-background/70 border-foreground/15 focus-visible:ring-inset focus-visible:ring-offset-0"
                  />
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="signup-modal-terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="signup-modal-terms" className="text-sm text-muted-foreground cursor-pointer leading-snug">
                    I agree to the{" "}
                    <TermsAndConditions>
                      <button
                        type="button"
                        className="text-primary hover:underline font-medium"
                      >
                        Terms and Conditions
                      </button>
                    </TermsAndConditions>
                    , including that Nightspy is not liable for event disruptions or issues.
                  </label>
                </div>
                <Button type="submit" className="w-full" disabled={isSignUpLoading || !termsAccepted}>
                  {isSignUpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            )}
          </TabsContent>
            </div>
          </div>
        </Tabs>

        {/* Soft darkness across the whole modal bottom until the form's end */}
        <div
          aria-hidden
          data-at-end={atPanelEnd ? "1" : "0"}
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-3xl bg-[linear-gradient(to_top,hsl(var(--background)/0.45),hsl(var(--background)/0))] transition-opacity duration-300",
            atPanelEnd ? "opacity-0" : "opacity-100"
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

type OAuthButtonsProps = {
  mode: "signin" | "signup";
  onOAuth: (provider: "google" | "apple") => void;
  googleLoading: boolean;
  appleLoading: boolean;
};

const OAuthButtons = ({ mode, onOAuth, googleLoading, appleLoading }: OAuthButtonsProps) => (
  <div className="space-y-2">
    <Button type="button" variant="outline" className="w-full" onClick={() => onOAuth("google")} disabled={googleLoading || appleLoading}>
      {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GoogleIcon /><span className="ml-2">{mode === "signup" ? "Sign up" : "Sign in"} with Google</span></>}
    </Button>
    <Button type="button" variant="outline" className="w-full" onClick={() => onOAuth("apple")} disabled={googleLoading || appleLoading}>
      {appleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><AppleIcon /><span className="ml-2">{mode === "signup" ? "Sign up" : "Sign in"} with Apple</span></>}
    </Button>
  </div>
);

const OrDivider = () => (
  <div className="relative my-4">
    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-primary/20" /></div>
    <div className="relative flex justify-center text-xs uppercase"><span className="rounded-full border border-primary/30 bg-background px-3 py-1 text-muted-foreground">OR</span></div>
  </div>
);
